import { call, put, takeEvery, takeLatest, select, all, fork } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  routeCardApi,
  SearchRouteCardsRequest,
  SearchRouteCardsResponse,
  CreateRouteCardRequest,
  RouteCard,
  RouteCardOperation,
  RouteCardOperationInput,
  WorkInstruction,
  ToolFixture,
  RouteCardDocument,
  Operation,
  Client,
  Part,
  RouteCardStatus,
} from '../../services/routeCardApi';
import {
  fetchRouteCardsStart,
  fetchRouteCardsSuccess,
  fetchRouteCardsFailure,
  fetchRouteCardByIdStart,
  fetchRouteCardByIdSuccess,
  fetchRouteCardByIdFailure,
  createRouteCardStart,
  createRouteCardSuccess,
  createRouteCardFailure,
  updateRouteCardStart,
  updateRouteCardSuccess,
  updateRouteCardFailure,
  deleteRouteCardStart,
  deleteRouteCardSuccess,
  deleteRouteCardFailure,
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
  fetchOperationsStart,
  fetchOperationsSuccess,
  fetchOperationsFailure,
  addOperationStart,
  addOperationSuccess,
  addOperationFailure,
  updateOperationStart,
  updateOperationSuccess,
  updateOperationFailure,
  deleteOperationStart,
  deleteOperationSuccess,
  deleteOperationFailure,
  reorderOperationsStart,
  reorderOperationsSuccess,
  reorderOperationsFailure,
  fetchOperationDetailsStart,
  fetchOperationDetailsSuccess,
  fetchOperationDetailsFailure,
  fetchDocumentsStart,
  fetchDocumentsSuccess,
  fetchDocumentsFailure,
  approveRouteCardStart,
  approveRouteCardSuccess,
  approveRouteCardFailure,
  rejectRouteCardStart,
  rejectRouteCardSuccess,
  rejectRouteCardFailure,
  setClientFilter,
} from '../reducers/routeCardSlice';
import { RootState } from '../index';

// Fetch Route Cards
function* fetchRouteCardsApi(action: PayloadAction<SearchRouteCardsRequest | undefined>): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { 
      currentPage, 
      pageSize, 
      searchTerm, 
      clientFilter, 
      partFilter, 
      statusFilter 
    } = state.routeCard;

    const params: SearchRouteCardsRequest = {
      pageNum: currentPage,
      pageSize: pageSize,
      sort: 'Name asc',
      filter: {
        ...(action.payload?.filter || {}),
        ...(clientFilter && { ClientId: clientFilter }),
        ...(partFilter && { PartId: partFilter }),
        ...(statusFilter && { RCTStatusId: statusFilter }),
        ...(searchTerm && { Name: searchTerm }),
        Active: true,
      },
      ...action.payload,
    };

    const response: SearchRouteCardsResponse = yield call(routeCardApi.getAll, params);
    yield put(fetchRouteCardsSuccess(response));
  } catch (error: any) {
    yield put(fetchRouteCardsFailure(error.message || 'Failed to fetch route cards'));
  }
}

// Fetch Route Card by ID
function* fetchRouteCardByIdApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const routeCard: RouteCard = yield call(routeCardApi.getById, action.payload);
    yield put(fetchRouteCardByIdSuccess(routeCard));
    
    // Also fetch operations for this route card
    yield put(fetchOperationsStart(action.payload));
  } catch (error: any) {
    yield put(fetchRouteCardByIdFailure(error.message || 'Failed to fetch route card'));
  }
}

// Create Route Card
function* createRouteCardApi(action: PayloadAction<CreateRouteCardRequest>): Generator<any, void, any> {
  try {
    const newRouteCard: RouteCard = yield call(routeCardApi.create, action.payload);
    yield put(createRouteCardSuccess(newRouteCard));

    // Refresh the route cards list to show the new item
    yield put(fetchRouteCardsStart());
  } catch (error: any) {
    yield put(createRouteCardFailure(error.message || 'Failed to create route card'));
  }
}

// Update Route Card
function* updateRouteCardApi(action: PayloadAction<Partial<RouteCard>>): Generator<any, void, any> {
  try {
    const updatedRouteCard: RouteCard = yield call(routeCardApi.update, action.payload);
    yield put(updateRouteCardSuccess(updatedRouteCard));
  } catch (error: any) {
    yield put(updateRouteCardFailure(error.message || 'Failed to update route card'));
  }
}

// Delete Route Card
function* deleteRouteCardApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(routeCardApi.delete, action.payload);
    yield put(deleteRouteCardSuccess(action.payload));
  } catch (error: any) {
    yield put(deleteRouteCardFailure(error.message || 'Failed to delete route card'));
  }
}

// Fetch Dropdown Data
function* fetchDropdownsApi(): Generator<any, void, any> {
  try {
    console.log('RouteCard Saga: Starting dropdown data fetch...');
    
    // Test each API call individually with try-catch
    console.log('RouteCard Saga: Fetching clients...');
    const clientsResponse = yield call(routeCardApi.getClients);
    console.log('RouteCard Saga: Clients response:', clientsResponse);
    
    console.log('RouteCard Saga: Fetching parts...');
    const partsResponse = yield call(routeCardApi.getParts);
    console.log('RouteCard Saga: Parts response:', partsResponse);
    
    console.log('RouteCard Saga: Fetching operations...');
    const operationsResponse = yield call(routeCardApi.getOperationsList);
    console.log('RouteCard Saga: Operations response:', operationsResponse);
    
    console.log('RouteCard Saga: Fetching statuses...');
    const statusesResponse = yield call(routeCardApi.getRouteCardStatuses);
    console.log('RouteCard Saga: Statuses response:', statusesResponse);

    console.log('RouteCard Saga: Raw responses received:', {
      clientsResponse: typeof clientsResponse,
      partsResponse: typeof partsResponse,
      operationsResponse: typeof operationsResponse,
      statusesResponse: typeof statusesResponse
    });

    // Handle response format the same way as Parts saga
    const extractData = (response: any, name: string) => {
      console.log(`RouteCard Saga: Processing ${name} response:`, response);
      
      // Parse response body if it's a string (like Parts saga does)
      let parsedResponse;
      if (response.body && typeof response.body === 'string') {
        try {
          parsedResponse = JSON.parse(response.body);
          console.log(`RouteCard Saga: Parsed ${name} response body:`, parsedResponse);
        } catch (error) {
          console.error(`RouteCard Saga: Failed to parse ${name} response body:`, error);
          parsedResponse = response;
        }
      } else {
        parsedResponse = response;
      }
      
      // The key fix: Always prioritize the data array from parsed response
      let data;
      if (parsedResponse && parsedResponse.data && Array.isArray(parsedResponse.data)) {
        // This is the correct path for API responses like {"data": [...], "total": 20}
        data = parsedResponse.data;
        console.log(`RouteCard Saga: Using parsedResponse.data array for ${name}, length:`, data.length);
      } else if (Array.isArray(parsedResponse)) {
        // Fallback: if the whole response is already an array
        data = parsedResponse;
        console.log(`RouteCard Saga: Using whole parsedResponse as array for ${name}, length:`, data.length);
      } else {
        // Last resort: empty array
        console.warn(`RouteCard Saga: No valid array found for ${name}, using empty array`);
        data = [];
      }
      
      console.log(`RouteCard Saga: Final ${name} data:`, data);
      console.log(`RouteCard Saga: Final ${name} length:`, data.length);
      
      return data;
    };

    const clients = extractData(clientsResponse, 'clients');
    const parts = extractData(partsResponse, 'parts');
    const operations = extractData(operationsResponse, 'operations');
    const routeCardStatuses = extractData(statusesResponse, 'routeCardStatuses');

    console.log('RouteCard Saga: After extraction:', {
      clients: Array.isArray(clients) ? `Array(${clients.length})` : typeof clients,
      parts: Array.isArray(parts) ? `Array(${parts.length})` : typeof parts,
      operations: Array.isArray(operations) ? `Array(${operations.length})` : typeof operations,
      routeCardStatuses: Array.isArray(routeCardStatuses) ? `Array(${routeCardStatuses.length})` : typeof routeCardStatuses
    });

    console.log('RouteCard Saga: Final dropdown data counts:', {
      clients: clients.length,
      parts: parts.length,
      operations: operations.length,
      routeCardStatuses: routeCardStatuses.length
    });

    // Debug first items if available
    if (clients.length > 0) {
      console.log('RouteCard Saga: First client:', clients[0]);
    }
    if (parts.length > 0) {
      console.log('RouteCard Saga: First part:', parts[0]);
    }
    if (routeCardStatuses.length > 0) {
      console.log('RouteCard Saga: First status:', routeCardStatuses[0]);
    }

    const payload = {
      clients: Array.isArray(clients) ? clients : [],
      parts: Array.isArray(parts) ? parts : [],
      operations: Array.isArray(operations) ? operations : [],
      routeCardStatuses: Array.isArray(routeCardStatuses) ? routeCardStatuses : []
    };
    
    console.log('RouteCard Saga: About to dispatch fetchDropdownsSuccess with payload:', payload);
    console.log('RouteCard Saga: Payload structure check:', {
      hasClients: 'clients' in payload,
      hasParts: 'parts' in payload,
      hasOperations: 'operations' in payload,
      hasRouteCardStatuses: 'routeCardStatuses' in payload,
      clientsIsArray: Array.isArray(payload.clients),
      partsIsArray: Array.isArray(payload.parts),
      operationsIsArray: Array.isArray(payload.operations),
      statusesIsArray: Array.isArray(payload.routeCardStatuses)
    });
    console.log('RouteCard Saga: Payload parts array length:', payload.parts.length);
    console.log('RouteCard Saga: Payload parts is array:', Array.isArray(payload.parts));
    console.log('RouteCard Saga: First few parts:', payload.parts.slice(0, 3));

    yield put(fetchDropdownsSuccess(payload));
  } catch (error: any) {
    console.error('RouteCard Saga: Dropdown fetch error:', error);
    yield put(fetchDropdownsFailure(error.message || 'Failed to fetch dropdown data'));
  }
}

// Fetch Route Card Operations
function* fetchOperationsApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const operations: RouteCardOperation[] = yield call(routeCardApi.getOperations, action.payload);
    yield put(fetchOperationsSuccess(operations));
  } catch (error: any) {
    yield put(fetchOperationsFailure(error.message || 'Failed to fetch operations'));
  }
}

// Add Operation
function* addOperationApi(action: PayloadAction<{ routeCardId: string; operation: Omit<RouteCardOperation, 'id' | 'RCTId'> }>): Generator<any, void, any> {
  try {
    const newOperation: RouteCardOperation = yield call(
      routeCardApi.addOperation, 
      action.payload.routeCardId, 
      action.payload.operation
    );
    yield put(addOperationSuccess(newOperation));
  } catch (error: any) {
    yield put(addOperationFailure(error.message || 'Failed to add operation'));
  }
}

// Update Operation
function* updateOperationApi(action: PayloadAction<RouteCardOperation>): Generator<any, void, any> {
  try {
    const updatedOperation: RouteCardOperation = yield call(routeCardApi.updateOperation, action.payload);
    yield put(updateOperationSuccess(updatedOperation));
  } catch (error: any) {
    yield put(updateOperationFailure(error.message || 'Failed to update operation'));
  }
}

// Delete Operation
function* deleteOperationApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(routeCardApi.deleteOperation, action.payload);
    yield put(deleteOperationSuccess(action.payload));
  } catch (error: any) {
    yield put(deleteOperationFailure(error.message || 'Failed to delete operation'));
  }
}

// Reorder Operations
function* reorderOperationsApi(action: PayloadAction<{ routeCardId: string; operations: { id: string; sequence: number }[] }>): Generator<any, void, any> {
  try {
    yield call(routeCardApi.reorderOperations, action.payload.routeCardId, action.payload.operations);
    
    // Fetch updated operations to confirm the new order
    const operations: RouteCardOperation[] = yield call(routeCardApi.getOperations, action.payload.routeCardId);
    yield put(reorderOperationsSuccess(operations));
  } catch (error: any) {
    yield put(reorderOperationsFailure(error.message || 'Failed to reorder operations'));
  }
}

// Fetch Operation Details
function* fetchOperationDetailsApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    // Fetch operation inputs, work instructions, and tool fixtures in parallel
    const [inputs, workInstructions, toolFixtures]: [RouteCardOperationInput[], WorkInstruction[], ToolFixture[]] = yield all([
      call(routeCardApi.getOperationInputs, action.payload),
      call(routeCardApi.getWorkInstructions, action.payload),
      call(routeCardApi.getToolFixtures, action.payload)
    ]);

    yield put(fetchOperationDetailsSuccess({
      inputs,
      workInstructions,
      toolFixtures
    }));
  } catch (error: any) {
    yield put(fetchOperationDetailsFailure(error.message || 'Failed to fetch operation details'));
  }
}

// Fetch Documents
function* fetchDocumentsApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const documents: RouteCardDocument[] = yield call(routeCardApi.getDocuments, action.payload);
    yield put(fetchDocumentsSuccess(documents));
  } catch (error: any) {
    yield put(fetchDocumentsFailure(error.message || 'Failed to fetch documents'));
  }
}

// Approve Route Card
function* approveRouteCardApi(action: PayloadAction<{ id: string; comment?: string }>): Generator<any, void, any> {
  try {
    const updatedRouteCard: RouteCard = yield call(routeCardApi.approve, action.payload.id, action.payload.comment);
    yield put(approveRouteCardSuccess(updatedRouteCard));
  } catch (error: any) {
    yield put(approveRouteCardFailure(error.message || 'Failed to approve route card'));
  }
}

// Reject Route Card
function* rejectRouteCardApi(action: PayloadAction<{ id: string; comment?: string }>): Generator<any, void, any> {
  try {
    const updatedRouteCard: RouteCard = yield call(routeCardApi.reject, action.payload.id, action.payload.comment);
    yield put(rejectRouteCardSuccess(updatedRouteCard));
  } catch (error: any) {
    yield put(rejectRouteCardFailure(error.message || 'Failed to reject route card'));
  }
}

// Fetch Parts by Client
function* fetchPartsByClientApi(action: PayloadAction<string | null>): Generator<any, void, any> {
  try {
    if (!action.payload) return;
    
    console.log('RouteCard Saga: Fetching parts for client:', action.payload);
    const partsResponse: any = yield call(routeCardApi.getParts, action.payload);
    console.log('RouteCard Saga: Parts by client response:', partsResponse);
    
    // Use the same extractData logic as the main dropdown fetch
    const extractData = (response: any, name: string) => {
      console.log(`RouteCard Saga: Processing ${name} response:`, response);
      
      // Parse response body if it's a string
      let parsedResponse;
      if (response.body && typeof response.body === 'string') {
        try {
          parsedResponse = JSON.parse(response.body);
          console.log(`RouteCard Saga: Parsed ${name} response body:`, parsedResponse);
        } catch (error) {
          console.error(`RouteCard Saga: Failed to parse ${name} response body:`, error);
          parsedResponse = response;
        }
      } else {
        parsedResponse = response;
      }
      
      // Extract the data array
      let data;
      if (parsedResponse && parsedResponse.data && Array.isArray(parsedResponse.data)) {
        data = parsedResponse.data;
        console.log(`RouteCard Saga: Using parsedResponse.data array for ${name}, length:`, data.length);
      } else if (Array.isArray(parsedResponse)) {
        data = parsedResponse;
        console.log(`RouteCard Saga: Using whole parsedResponse as array for ${name}, length:`, data.length);
      } else {
        console.warn(`RouteCard Saga: No valid array found for ${name}, using empty array`);
        data = [];
      }
      
      return data;
    };
    
    const parts = extractData(partsResponse, 'parts-by-client');
    console.log('RouteCard Saga: Extracted parts for client:', parts.length);
    
    // Update parts in dropdown data
    const state: RootState = yield select();
    const { clients, operations, routeCardStatuses } = state.routeCard;
    
    yield put(fetchDropdownsSuccess({
      clients,
      parts,
      operations,
      routeCardStatuses
    }));
  } catch (error: any) {
    console.error('Failed to fetch parts by client:', error);
  }
}

// Watch for filter changes
function* watchFilters(): Generator<any, void, any> {
  yield takeLatest(['routeCard/setSearchTerm', 'routeCard/setClientFilter', 'routeCard/setPartFilter', 'routeCard/setStatusFilter'], function* () {
    yield put(fetchRouteCardsStart());
  });
}

// Watch for pagination changes
function* watchPagination(): Generator<any, void, any> {
  yield takeLatest(['routeCard/setPage', 'routeCard/setPageSize'], function* () {
    yield put(fetchRouteCardsStart());
  });
}

// Watch for client filter changes to fetch parts
function* watchClientFilter(): Generator<any, void, any> {
  yield takeEvery(setClientFilter.type, fetchPartsByClientApi);
}

// Root Route Card Saga
export default function* routeCardSaga(): Generator<any, void, any> {
  // Main API calls
  yield takeEvery(fetchRouteCardsStart.type, fetchRouteCardsApi);
  yield takeEvery(fetchRouteCardByIdStart.type, fetchRouteCardByIdApi);
  yield takeEvery(createRouteCardStart.type, createRouteCardApi);
  yield takeEvery(updateRouteCardStart.type, updateRouteCardApi);
  yield takeEvery(deleteRouteCardStart.type, deleteRouteCardApi);

  // Dropdown data
  yield takeEvery(fetchDropdownsStart.type, fetchDropdownsApi);

  // Operations management
  yield takeEvery(fetchOperationsStart.type, fetchOperationsApi);
  yield takeEvery(addOperationStart.type, addOperationApi);
  yield takeEvery(updateOperationStart.type, updateOperationApi);
  yield takeEvery(deleteOperationStart.type, deleteOperationApi);
  yield takeEvery(reorderOperationsStart.type, reorderOperationsApi);

  // Operation details
  yield takeEvery(fetchOperationDetailsStart.type, fetchOperationDetailsApi);

  // Documents
  yield takeEvery(fetchDocumentsStart.type, fetchDocumentsApi);

  // Status management
  yield takeEvery(approveRouteCardStart.type, approveRouteCardApi);
  yield takeEvery(rejectRouteCardStart.type, rejectRouteCardApi);

  // Background tasks
  yield fork(watchFilters);
  yield fork(watchPagination);
  yield fork(watchClientFilter);
} 